import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getMyBusiness,
  createBusiness,
  updateMyBusiness,
  getMySubscription,
  cancelSubscription,
  getMyBusinessAnalytics,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import {
  Store,
  Save,
  Loader2,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  CreditCard,
  Eye,
  MousePointerClick,
  TrendingUp,
  TrendingDown,
  Phone,
  Globe,
  Facebook,
  Instagram,
  Twitter,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Creative & Event Services',
  'Beauty & Personal Care',
  'Food & Dining',
  'Retail & Shopping',
  'Auto Services'
];

const SUBCATEGORIES = {
  'Creative & Event Services': [
    'Photography & Videography',
    'Event Planning & Coordination',
    'DJ & Music Services',
    'Decor, Backdrops & Rentals',
    'Catering & Mobile Food',
    'Makeup Artists & Hair Stylists',
    'Content Creators & Social Media',
    'Graphic Design & Branding',
    'Creative Workshops & Classes'
  ],
  'Beauty & Personal Care': [
    'Hair Stylists (Women)',
    'Braiders & Loc Specialists',
    'Barbers',
    'Nail Technicians',
    'Makeup Artists'
  ],
  'Food & Dining': [
    'Restaurants',
    'Food Trucks',
    'Catering Services',
    'Grocery & Markets',
    'Bakeries & Desserts',
    'Coffee & Cafes',
    'Bars & Nightlife'
  ],
  'Retail & Shopping': [
    'Boutiques & Clothing',
    'Grocery Stores',
    'Specialty & Gift Shops',
    'Electronics & Tech',
    'Home & Furniture',
    'Health & Wellness Products'
  ],
  'Auto Services': [
    'Mechanics & Repair',
    'Auto Detailing',
    'Towing Services',
    'Tire Shops',
    'Oil Change & Maintenance',
    'Body Shops & Collision'
  ]
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [business, setBusiness] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    description: '',
    phone: '',
    website: '',
    address: '',
    facebook: '',
    instagram: '',
    twitter: '',
    image_url: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [businessRes, subRes] = await Promise.all([
        getMyBusiness(),
        getMySubscription(),
      ]);
      
      if (businessRes.data) {
        setBusiness(businessRes.data);
        setFormData({
          name: businessRes.data.name || '',
          category: businessRes.data.category || '',
          subcategory: businessRes.data.subcategory || '',
          description: businessRes.data.description || '',
          phone: businessRes.data.phone || '',
          website: businessRes.data.website || '',
          address: businessRes.data.address || '',
          facebook: businessRes.data.facebook || '',
          instagram: businessRes.data.instagram || '',
          twitter: businessRes.data.twitter || '',
          image_url: businessRes.data.image_url || '',
        });
        
        // Fetch analytics if business exists
        try {
          const analyticsRes = await getMyBusinessAnalytics();
          if (analyticsRes.data) {
            setAnalytics(analyticsRes.data);
          }
        } catch (e) {
          // Analytics might not be available yet
        }
      }
      
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData((prev) => ({ ...prev, category: value, subcategory: '' }));
  };

  const handleSubcategoryChange = (value) => {
    setFormData((prev) => ({ ...prev, subcategory: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category || !formData.description) {
      toast.error('Please fill in required fields (Name, Category, Description)');
      return;
    }

    setSaving(true);
    try {
      if (business) {
        const res = await updateMyBusiness(formData);
        setBusiness(res.data);
        toast.success('Business updated successfully!');
      } else {
        const res = await createBusiness(formData);
        setBusiness(res.data);
        toast.success('Business created successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save business');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await cancelSubscription();
      setSubscription({ ...subscription, status: 'cancelled' });
      setBusiness({ ...business, is_published: false, subscription_status: 'cancelled' });
      toast.success('Subscription cancelled. Your listing has been unpublished.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const [showStats, setShowStats] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 noise-bg" data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">
            Business Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.name}! Manage your business listing below.
          </p>
        </div>

        {/* Subscription Status */}
        <div className="glass-card rounded-2xl border border-border p-6 mb-8" data-testid="subscription-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold mb-1">Subscription Status</h2>
              {subscription ? (
                <div className="flex items-center gap-3">
                  <Badge
                    variant={subscription.status === 'active' ? 'default' : 'secondary'}
                    className={subscription.status === 'active' ? 'bg-green-500/20 text-green-500' : ''}
                  >
                    {subscription.status === 'active' ? (
                      <CheckCircle className="w-3 h-3 mr-1" />
                    ) : (
                      <XCircle className="w-3 h-3 mr-1" />
                    )}
                    {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                  </Badge>
                  <Badge variant="outline" className="capitalize">
                    {subscription.plan_id} Plan
                  </Badge>
                  {subscription.is_featured && (
                    <Badge className="bg-primary/20 text-primary">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Featured
                    </Badge>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No active subscription. Subscribe to publish your listing.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {subscription?.status === 'active' ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="cancel-sub-btn">
                      <XCircle className="w-4 h-4" />
                      Cancel
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Your listing will be unpublished immediately. You can resubscribe at any time to restore it.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancelSubscription}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={cancelling}
                      >
                        {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel Subscription'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                <Link to="/pricing">
                  <Button className="gap-2 rounded-xl" data-testid="subscribe-btn">
                    <CreditCard className="w-4 h-4" />
                    Subscribe
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Listing Status */}
          {business && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm">
                {business.is_published ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-green-500">Your listing is live</span>
                    <Link
                      to="/"
                      className="text-primary hover:underline ml-2 inline-flex items-center gap-1"
                    >
                      View in Directory <ExternalLink className="w-3 h-3" />
                    </Link>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    <span className="text-yellow-500">
                      Your listing is not visible. Subscribe to publish.
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* See Your Stats CTA Card */}
        {business && (
          <div 
            className="glass-card rounded-2xl border border-accent/30 p-6 mb-8 cursor-pointer hover:border-accent/60 transition-colors"
            onClick={() => setShowStats(!showStats)}
            data-testid="see-stats-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">See Your Stats</h2>
                  <p className="text-sm text-muted-foreground">
                    {analytics ? `${analytics.total_views} views · ${analytics.total_clicks} clicks` : 'Track your listing performance'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="gap-2 border-accent/50 text-accent hover:bg-accent/10"
                data-testid="toggle-stats-btn"
              >
                <Eye className="w-4 h-4" />
                {showStats ? 'Hide Stats' : 'View Stats'}
              </Button>
            </div>
          </div>
        )}

        {/* Analytics Section - Expanded */}
        {business && showStats && (
          <div className="glass-card rounded-2xl border border-border p-6 mb-8 animate-fade-in-up" data-testid="analytics-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Performance Analytics</h2>
                <p className="text-sm text-muted-foreground">Track how your listing is performing</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Total Views */}
              <div className="bg-secondary/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-primary" />
                  <span className="text-xs text-muted-foreground">Total Views</span>
                </div>
                <div className="text-2xl font-bold">{analytics?.total_views || 0}</div>
              </div>

              {/* Total Clicks */}
              <div className="bg-secondary/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MousePointerClick className="w-4 h-4 text-accent" />
                  <span className="text-xs text-muted-foreground">Total Clicks</span>
                </div>
                <div className="text-2xl font-bold">{analytics?.total_clicks || 0}</div>
              </div>

              {/* 7 Day Views */}
              <div className="bg-secondary/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Last 7 Days</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">{analytics?.views_7d || 0}</span>
                  {analytics?.views_change_7d !== 0 && analytics?.views_change_7d && (
                    <span className={`text-xs flex items-center ${analytics.views_change_7d > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {analytics.views_change_7d > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                      {Math.abs(analytics.views_change_7d)}%
                    </span>
                  )}
                </div>
              </div>

              {/* 30 Day Views */}
              <div className="bg-secondary/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Last 30 Days</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">{analytics?.views_30d || 0}</span>
                  {analytics?.views_change_30d !== 0 && analytics?.views_change_30d && (
                    <span className={`text-xs flex items-center ${analytics.views_change_30d > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {analytics.views_change_30d > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                      {Math.abs(analytics.views_change_30d)}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Click Breakdown */}
            <div className="border-t border-border pt-4">
              <h3 className="text-sm font-medium mb-3">Outbound Clicks</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-secondary/20 rounded-lg p-3 text-center">
                  <Phone className="w-4 h-4 mx-auto mb-1 text-primary" />
                  <div className="text-lg font-bold">{analytics?.click_breakdown?.phone || 0}</div>
                  <div className="text-xs text-muted-foreground">Phone Calls</div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3 text-center">
                  <Globe className="w-4 h-4 mx-auto mb-1 text-primary" />
                  <div className="text-lg font-bold">{analytics?.click_breakdown?.website || 0}</div>
                  <div className="text-xs text-muted-foreground">Website</div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3 text-center">
                  <Facebook className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                  <div className="text-lg font-bold">{analytics?.click_breakdown?.facebook || 0}</div>
                  <div className="text-xs text-muted-foreground">Facebook</div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3 text-center">
                  <Instagram className="w-4 h-4 mx-auto mb-1 text-pink-500" />
                  <div className="text-lg font-bold">{analytics?.click_breakdown?.instagram || 0}</div>
                  <div className="text-xs text-muted-foreground">Instagram</div>
                </div>
                <div className="bg-secondary/20 rounded-lg p-3 text-center">
                  <Twitter className="w-4 h-4 mx-auto mb-1 text-sky-500" />
                  <div className="text-lg font-bold">{analytics?.click_breakdown?.twitter || 0}</div>
                  <div className="text-xs text-muted-foreground">Twitter</div>
                </div>
              </div>
            </div>

            {/* Top Referrer Categories */}
            {analytics?.top_referrer_categories?.length > 0 && (
              <div className="border-t border-border pt-4 mt-4">
                <h3 className="text-sm font-medium mb-3">Traffic Sources</h3>
                <p className="text-xs text-muted-foreground mb-3">Where visitors found your listing</p>
                <div className="flex flex-wrap gap-2">
                  {analytics.top_referrer_categories.map((ref, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {ref.category}: {ref.count} views
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Privacy Note */}
            <div className="border-t border-border pt-4 mt-4">
              <p className="text-xs text-muted-foreground text-center">
                Stats are anonymous and aggregated. No personal visitor data is shared.
              </p>
            </div>
          </div>
        )}

        {/* Business Form */}
        <div className="glass-card rounded-2xl border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Store className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {business ? 'Edit Business' : 'Create Business'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {business ? 'Update your listing information' : 'Set up your business profile'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="business-form">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Business Name"
                  className="h-12 bg-background/50"
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="h-12 bg-background/50" data-testid="select-category">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subcategory - show if category has subcategories */}
            {formData.category && SUBCATEGORIES[formData.category]?.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="subcategory">Service Type *</Label>
                <Select value={formData.subcategory} onValueChange={handleSubcategoryChange}>
                  <SelectTrigger className="h-12 bg-background/50" data-testid="select-subcategory">
                    <SelectValue placeholder="Select your service type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBCATEGORIES[formData.category].map((sub) => (
                      <SelectItem key={sub} value={sub}>
                        {sub}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your business, services, and what makes you unique..."
                className="min-h-[120px] bg-background/50"
                data-testid="input-description"
              />
            </div>

            {/* Contact Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(520) 555-0123"
                  className="h-12 bg-background/50"
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://yourbusiness.com"
                  className="h-12 bg-background/50"
                  data-testid="input-website"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, Tucson, AZ 85701"
                className="h-12 bg-background/50"
                data-testid="input-address"
              />
            </div>

            {/* Social Links */}
            <div>
              <Label className="mb-3 block">Social Media Links</Label>
              <div className="grid md:grid-cols-3 gap-4">
                <Input
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  placeholder="Facebook URL"
                  className="h-12 bg-background/50"
                  data-testid="input-facebook"
                />
                <Input
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="Instagram URL"
                  className="h-12 bg-background/50"
                  data-testid="input-instagram"
                />
                <Input
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleChange}
                  placeholder="Twitter URL"
                  className="h-12 bg-background/50"
                  data-testid="input-twitter"
                />
              </div>
            </div>

            {/* Image URL */}
            <div className="space-y-2">
              <Label htmlFor="image_url">Business Image URL</Label>
              <Input
                id="image_url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://example.com/your-business-image.jpg"
                className="h-12 bg-background/50"
                data-testid="input-image"
              />
              <p className="text-xs text-muted-foreground">
                Enter a URL to an image that represents your business
              </p>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                className="h-12 px-8 rounded-xl gap-2"
                disabled={saving}
                data-testid="save-business-btn"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {business ? 'Update Business' : 'Create Business'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
